import { Button, TextField } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import BasicModal from '../modal/BasicModal'
import DiscountForm from './DiscountForm'
import TaxesForm from './TaxesForm'
import {
    confirmOrder,
    copyOrder,
    returnOrderToDraft,
    returnOrderToQuote,
    sendToApproval,
} from '../../store/actions/orderAction'
import { OrderStatusList } from '../../constants/orderType'
import { roleListId, roleType } from '../../config/roleList'
import { checkUserRole } from '../../common/checkUserRole'
import AdditionalChargeForm from './AdditionalChargeForm'

const returnDraftList = [
    OrderStatusList.QUOTE,
    OrderStatusList.IN_PROGRESS,
    OrderStatusList.READY,
]

const OrderEditList = ({ order, orderItemList }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [discountOpen, setDiscountOpen] = useState(false)
    const [taxesOpen, setTaxesOpen] = useState(false)
    const [additionalChargeOpen, setAdditionalChargeOpen] = useState(false)
    const [copyOpen, setCopyOpen] = useState(false)
    const [copyPONumber, setCopyPONumber] = useState('')
    const { userInfo } = useSelector((state) => state.auth)
    const { roleList } = useSelector((state) => state.user)

    const handleReturnOrderToQuote = useCallback(() => {
        if (window.confirm('Want to return order to new Quote')) {
            const values = {
                itemIds: order.quoteType === 4 ? orderItemList : [],
            }
            dispatch(returnOrderToQuote(order.id, values))
        }
    }, [dispatch, order, orderItemList])

    const handleReturnOrderToDraft = useCallback(() => {
        if (window.confirm('Want to return order to Draft')) {
            const values = {
                itemIds: order.quoteType === 4 ? orderItemList : [],
            }
            dispatch(returnOrderToDraft(order.id, values))
        }
    }, [dispatch, order, orderItemList])

    const handleCopyOrder = useCallback(() => {
        setCopyPONumber('')
        setCopyOpen(true)
    }, [])

    const handleCreateCopy = useCallback(() => {
        const poNumber = copyPONumber.trim()
        if (!poNumber) return

        dispatch(copyOrder(order.id, poNumber, navigate))
        setCopyOpen(false)
    }, [copyPONumber, dispatch, navigate, order])

    const propValues = useMemo(() => {
        const values = {
            id: order?.id,
            deliveryCost: order?.deliveryCost,
            discountPercentage: order?.discountPercentage,
        }
        return values
    }, [order])

    return (
        <>
            <BasicModal open={discountOpen} setOpen={setDiscountOpen}>
                <DiscountForm
                    propValues={propValues}
                    handleClose={setDiscountOpen}
                />
            </BasicModal>
            <BasicModal open={taxesOpen} setOpen={setTaxesOpen}>
                <TaxesForm />
            </BasicModal>
            <BasicModal
                open={additionalChargeOpen}
                setOpen={setAdditionalChargeOpen}
            >
                <AdditionalChargeForm />
            </BasicModal>
            <BasicModal open={copyOpen} setOpen={setCopyOpen}>
                <form
                    aria-label="Copy order dialog"
                    onSubmit={(event) => {
                        event.preventDefault()
                        handleCreateCopy()
                    }}
                    style={{ display: 'grid', gap: 16, minWidth: 320 }}
                >
                    <div>
                        <h2 style={{ margin: 0 }}>Copy Order</h2>
                        <p style={{ marginBottom: 0 }}>
                            Enter a new PO name for the copied quote.
                        </p>
                    </div>
                    <TextField
                        autoFocus
                        fullWidth
                        inputProps={{ maxLength: 30 }}
                        label="New PO Name"
                        value={copyPONumber}
                        onChange={(event) =>
                            setCopyPONumber(event.target.value)
                        }
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            disabled={!copyPONumber.trim()}
                            type="submit"
                            variant="contained"
                        >
                            Create Copy
                        </Button>
                    </div>
                </form>
            </BasicModal>
            <div className="order-edit-list-container">
                <div className="order-edit-list-box">
                    {order?.id &&
                        order.orderType !== OrderStatusList.CANCELLED &&
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ORDER_COPY,
                            roleType.CREATE
                        ) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={handleCopyOrder}
                            >
                                Copy Order
                            </Button>
                        )}
                    {order?.orderType === OrderStatusList.QUOTE &&
                        order?.quoteType === 1 && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() => setDiscountOpen(true)}
                                >
                                    Edit Discount & Delivery
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setTaxesOpen(true)}
                                >
                                    Edit Order Taxes
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() =>
                                        setAdditionalChargeOpen(true)
                                    }
                                >
                                    Edit Additional Charge
                                </Button>
                                {checkUserRole(
                                    userInfo,
                                    roleList,
                                    roleListId.ORDER,
                                    roleType.APPROVAL
                                ) && (
                                    <>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        'Want to Send for approval'
                                                    )
                                                ) {
                                                    dispatch(
                                                        sendToApproval(order.id)
                                                    )
                                                }
                                            }}
                                        >
                                            Send for approval
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        'Confirm Order?'
                                                    )
                                                ) {
                                                    dispatch(
                                                        confirmOrder(
                                                            order.id,
                                                            order
                                                        )
                                                    )
                                                }
                                            }}
                                        >
                                            Confirm Order
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            onClick={() =>
                                                navigate(
                                                    `/productorders/${order.id}`
                                                )
                                            }
                                        >
                                            Edit Order
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    {returnDraftList.includes(order?.orderType) &&
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ORDER,
                            roleType.APPROVAL
                        ) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={handleReturnOrderToDraft}
                            >
                                Return Order to Draft
                            </Button>
                        )}
                    {returnDraftList.includes(order?.orderType) &&
                        order?.quoteType !== 1 &&
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ORDER,
                            roleType.APPROVAL
                        ) && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={handleReturnOrderToQuote}
                            >
                                Return Order to new Quote
                            </Button>
                        )}
                </div>
            </div>
        </>
    )
}

export default OrderEditList
